import { ChevronDown, CircleHelp, LogOut, PanelLeftClose, PanelLeftOpen } from 'lucide-react'
import { NavLink, useLocation, useNavigate } from 'react-router'
import { useId } from 'react'

import { cn } from '@/lib/cn'
import { useUiStore } from '@/stores/uiStore'
import { useAuthStore } from '@/features/auth/stores/authStore'
import { useModules } from '@/features/organizations/hooks/useModules'
import { Avatar } from '@/components/ui/Avatar'
import { Badge } from '@/components/ui/Badge'
import { Button, IconButton } from '@/components/ui/Button'
import { Tooltip } from '@/components/ui/Tooltip'
import { Logo } from './Logo'
import {
  CROSS_PROJECT_NAVIGATION,
  PLATFORM_NAVIGATION_ITEM,
  PRIMARY_NAVIGATION,
  SECONDARY_NAVIGATION,
  type NavigationGroup,
  type NavigationItem,
} from './navigation'

interface SidebarNavItemProps {
  item: NavigationItem
  collapsed: boolean
  onNavigate?: () => void
}

function SidebarNavItem({ item, collapsed, onNavigate }: SidebarNavItemProps) {
  const Icon = item.icon

  const link = (
    <NavLink
      to={item.to}
      onClick={onNavigate}
      aria-disabled={item.comingSoon || undefined}
      // Moduler der endnu ikke findes, må ikke kunne åbnes. En knap der ikke
      // gør noget er værre end en knap der tydeligt er på vej.
      onClickCapture={(event) => {
        if (item.comingSoon) event.preventDefault()
      }}
      className={({ isActive }) =>
        cn(
          'group relative flex items-center gap-3 rounded-[var(--radius-control)] px-3 py-2',
          'text-[13px] font-medium transition-colors duration-[var(--duration-hover)]',
          'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-400',
          collapsed && 'justify-center px-0',
          item.comingSoon
            ? 'cursor-not-allowed text-text-muted/70 hover:bg-overlay-subtle'
            : isActive
              ? [
                  'bg-gradient-to-r from-brand-500/22 to-info-500/12 text-text-primary',
                  'shadow-[inset_0_1px_0_rgb(255_255_255/0.05)]',
                ]
              : 'text-text-secondary hover:bg-overlay-soft hover:text-text-primary',
        )
      }
    >
      {({ isActive }) => (
        <>
          {/* Venstre indikator på det aktive punkt. */}
          {isActive && !item.comingSoon && (
            <span
              aria-hidden="true"
              className={cn(
                'absolute left-0 top-1/2 h-5 w-0.5 -translate-y-1/2 rounded-r-full bg-brand-400',
                'shadow-[0_0_10px_var(--color-brand-400)]',
                collapsed && 'left-0',
              )}
            />
          )}

          <Icon
            className={cn(
              'size-4 shrink-0 transition-colors',
              isActive && !item.comingSoon ? 'text-brand-300' : 'text-current',
            )}
            aria-hidden="true"
          />

          {!collapsed && (
            <>
              <span className="truncate">{item.label}</span>
              {item.comingSoon && (
                <Badge tone="neutral" className="ml-auto shrink-0 text-[10px]">
                  På vej
                </Badge>
              )}
            </>
          )}
        </>
      )}
    </NavLink>
  )

  // Sammenklappet har punkterne kun et ikon, så navnet skal kunne ses på hover.
  return collapsed ? (
    <Tooltip
      content={item.comingSoon ? `${item.label} (på vej)` : item.label}
      side="right"
    >
      <div>{link}</div>
    </Tooltip>
  ) : (
    link
  )
}

interface SidebarGroupProps {
  group: NavigationGroup
  collapsed: boolean
  onNavigate?: () => void
}

/**
 * En foldbar gruppe i menuen.
 *
 * Tilstanden ligger i uiStore og huskes derfor mellem sessioner. Den er
 * bevidst *ikke* lokal state: sidebaren findes to gange i DOM'en — som fast
 * menu på desktop og som drawer på mobil — og to kopier med hver sin
 * hukommelse ville stå og modsige hinanden.
 */
function SidebarGroup({ group, collapsed, onNavigate }: SidebarGroupProps) {
  const { pathname } = useLocation()
  const preferOpen = useUiStore((state) => state.crossProjectNavOpen)
  const toggle = useUiStore((state) => state.toggleCrossProjectNav)
  const { isEnabled } = useModules()
  // Sidebaren renderes to steder, så et fast id ville optræde dobbelt.
  const listId = `${useId()}${group.id}`
  const Icon = group.icon

  const items = group.items.filter((item) => item.module === undefined || isEnabled(item.module))

  // Står man inde i et af punkterne, skal gruppen være åben uanset
  // præferencen — ellers gemmer menuen den side, man faktisk er på.
  const containsActiveRoute = items.some((item) => pathname.startsWith(item.to))
  const expanded = preferOpen || containsActiveRoute

  const trigger = (
    <button
      type="button"
      onClick={toggle}
      aria-expanded={expanded}
      aria-controls={listId}
      className={cn(
        'flex w-full items-center gap-3 rounded-[var(--radius-control)] px-3 py-2',
        'text-[13px] font-medium text-text-secondary transition-colors duration-[var(--duration-hover)]',
        'hover:bg-overlay-soft hover:text-text-primary',
        'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-400',
        collapsed && 'justify-center px-0',
      )}
    >
      <Icon className="size-4 shrink-0" aria-hidden="true" />

      {!collapsed && (
        <>
          <span className="truncate">{group.label}</span>
          <ChevronDown
            className={cn(
              'ml-auto size-3.5 shrink-0 transition-transform duration-[var(--duration-hover)]',
              expanded && 'rotate-180',
            )}
            aria-hidden="true"
          />
        </>
      )}
    </button>
  )

  return (
    <div className="mt-1">
      {collapsed ? (
        <Tooltip content={group.label} side="right">
          <div>{trigger}</div>
        </Tooltip>
      ) : (
        trigger
      )}

      {/* Listen bliver stående i DOM'en, så `aria-controls` altid peger på
          noget. `hidden` som attribut og ikke kun som klasse: semantikken må
          ikke afhænge af, at stilarket er nået frem. */}
      <ul
        id={listId}
        hidden={!expanded}
        className={cn(
          'mt-0.5 flex-col gap-0.5',
          !collapsed && 'ml-[1.4rem] border-l border-line-subtle pl-2',
          expanded ? 'flex' : 'hidden',
        )}
      >
        {items.map((item) => (
          <li key={item.to}>
            <SidebarNavItem item={item} collapsed={collapsed} onNavigate={onNavigate} />
          </li>
        ))}

        {!collapsed && (
          <li className="px-3 pb-1 pt-1.5 text-[11px] leading-snug text-text-muted">
            {group.description}
          </li>
        )}
      </ul>
    </div>
  )
}

export interface SidebarProps {
  /** Sat når sidebaren vises som drawer på mobil. */
  onNavigate?: () => void
  className?: string
  /** Tvinger udvidet visning i mobil-drawer, uanset desktop-præferencen. */
  forceExpanded?: boolean
}

export function Sidebar({ onNavigate, className, forceExpanded = false }: SidebarProps) {
  const collapsed = useUiStore((state) => state.sidebarCollapsed) && !forceExpanded
  const toggleSidebar = useUiStore((state) => state.toggleSidebar)
  const user = useAuthStore((state) => state.user)
  const signOut = useAuthStore((state) => state.signOut)
  const navigate = useNavigate()

  async function handleSignOut() {
    await signOut()
    void navigate('/login', { replace: true })
  }

  return (
    <div
      className={cn(
        'flex h-full flex-col border-r border-line-subtle bg-app-900/70 backdrop-blur-xl',
        className,
      )}
    >
      <div
        className={cn(
          'flex h-16 shrink-0 items-center gap-2 px-4',
          collapsed && 'justify-center px-0',
        )}
      >
        <Logo collapsed={collapsed} />
      </div>

      <nav aria-label="Hovednavigation" className="flex-1 overflow-y-auto px-3 pb-3">
        <ul className="flex flex-col gap-0.5">
          {PRIMARY_NAVIGATION.map((item) => (
            <li key={item.to}>
              <SidebarNavItem item={item} collapsed={collapsed} onNavigate={onNavigate} />
            </li>
          ))}
        </ul>

        <SidebarGroup
          group={CROSS_PROJECT_NAVIGATION}
          collapsed={collapsed}
          onNavigate={onNavigate}
        />

        <ul
          className={cn(
            'mt-2 flex flex-col gap-0.5 border-t border-line-subtle pt-2',
            collapsed && 'items-stretch',
          )}
        >
          {SECONDARY_NAVIGATION.map((item) => (
            <li key={item.to}>
              <SidebarNavItem item={item} collapsed={collapsed} onNavigate={onNavigate} />
            </li>
          ))}

          {/* Kun for leverandørens egne folk. Punktet er ikke en adgang —
              hvert kald derind kontrolleres i backend — men det skal heller
              ikke stå og friste alle andre. */}
          {user?.isPlatformAdmin && (
            <li>
              <SidebarNavItem
                item={PLATFORM_NAVIGATION_ITEM}
                collapsed={collapsed}
                onNavigate={onNavigate}
              />
            </li>
          )}
        </ul>
      </nav>

      <div className="shrink-0 border-t border-line-subtle p-3">
        <div className={cn('flex items-center gap-2', collapsed && 'flex-col')}>
          <NavLink
            to="/app/settings/profile"
            onClick={onNavigate}
            className={cn(
              'flex min-w-0 flex-1 items-center gap-2.5 rounded-[var(--radius-control)] p-1.5',
              'text-left transition-colors hover:bg-overlay-soft',
              'focus-visible:outline-2 focus-visible:outline-brand-400',
              collapsed && 'flex-none justify-center',
            )}
            aria-label="Åbn min profil"
          >
            <Avatar
              name={user?.fullName ?? 'Ukendt bruger'}
              initials={user?.initials}
              src={user?.avatarUrl}
              size="sm"
            />
            {!collapsed && (
              <span className="min-w-0">
                <span className="block truncate text-[13px] font-medium text-text-primary">
                  {user?.fullName ?? '—'}
                </span>
                <span className="block truncate text-[11px] text-text-muted">
                  {user?.activeOrganization?.name ?? 'Ingen organisation'}
                </span>
              </span>
            )}
          </NavLink>

          {!collapsed && (
            <>
              <Button
                variant="ghost"
                size="icon-sm"
                aria-label="Hjælp"
                title="Hjælp"
                onClick={onNavigate}
                asChild
              >
                <NavLink to="/app/hjaelp">
                  <CircleHelp />
                </NavLink>
              </Button>
              <IconButton
                label="Log ud"
                icon={<LogOut />}
                size="icon-sm"
                onClick={() => void handleSignOut()}
              />
            </>
          )}
        </div>

        {/* Sammenklapning er en desktopfunktion; i drawer-visning giver den ikke mening. */}
        {!forceExpanded && (
          <div className={cn('mt-2 hidden lg:flex', collapsed ? 'justify-center' : 'justify-end')}>
            <IconButton
              label={collapsed ? 'Udvid sidebar' : 'Klap sidebar sammen'}
              icon={collapsed ? <PanelLeftOpen /> : <PanelLeftClose />}
              size="icon-sm"
              onClick={toggleSidebar}
              aria-expanded={!collapsed}
            />
          </div>
        )}
      </div>
    </div>
  )
}
